import React, { useCallback, useEffect, useMemo, useState } from 'react';

import cn from 'classnames';
import {
  MdAttachMoney,
  MdContentPaste,
  MdDashboard,
  MdEmail,
  MdFace,
  MdFormatListBulleted,
  MdHowToReg,
  MdInfoOutline,
  MdPeopleOutline,
  MdPlace,
  MdSettings,
  MdSupervisorAccount,
  MdToday,
} from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'redux-first-router-link';
import type { RootState } from 'src/root/store';

import ActivityIcon from '@assets/images/Activities.svg';
import {
  RECHARTER_DATE_RESTRICTION,
  SHOW_ACTIVITIES_MENU,
  SHOW_COUNSELED_YOUTH,
  SHOW_DASHBOARD,
  SHOW_MB_COUNSELORS,
  SHOW_MESSAGING,
  SHOW_PERMISSION_MANAGER,
} from '@config/config';
import { isSelectedOrganizationValidSel, unitTypeIdSel } from '@context';
import { pathnameSel } from '@location';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { overrideShowUnitPaymentLogsSel } from '@modules/paymentLogs/duck/selectors';
import {
  getNewProgressReportUri,
  getNewRosterReportUri,
} from '@modules/reports/customReports/duck/utils';
import { isInactiveUnitSel } from '@modules/unit/duck/selectorsTyped';
import { Menu } from '@shared/components';
import { navigateToReports, navigateToSettings } from '@shared/duck/actions';
import {
  childUserIdSel,
  isCouncilOrgActiveSel,
  isKey3Sel,
  isParentGuardianSel,
} from '@shared/duck/selectors';
import { isRecharterPeriod } from '@shared/utils';
import { unitInfoSel } from '@unit';
import { permissions, permissionsSel, userDataInitializedSel } from '@user';

import { isReportsRouteSel, isSettingsRouteSel } from '../../duck';
import SettingsOptions from '../SettingsOptions';
import ForumMenuItem from './ForumMenuItem';
import styles from './Nav.less';
import NavMenuItem from './NavMenuItem';
import NavMenuItemContent from './NavMenuItemContent';
import ScoutbookLegacyMenuItem from './ScoutbookLegacyMenuItem';

const SubMenu = Menu.SubMenu;
const MenuItem = Menu.Item;

interface NavProps {
  isExpanded: boolean;
  isMobile: boolean;
}

const Nav: React.FC<NavProps> = ({ isExpanded, isMobile }) => {
  const dispatch = useDispatch();

  const loading = !useSelector(userDataInitializedSel);
  const isSettingsRoute = useSelector(isSettingsRouteSel);
  const isReportsRoute = useSelector(isReportsRouteSel);
  const pathname = useSelector(pathnameSel);
  const isInactiveUnit = useSelector(isInactiveUnitSel);
  const unitTypeId = useSelector(unitTypeIdSel);
  const overrideShowUnitPaymentLogs = useSelector(
    overrideShowUnitPaymentLogsSel,
  );
  const userPermissions = useSelector(permissionsSel);
  const isSelectedUnitValid = useSelector(isSelectedOrganizationValidSel);
  const isParentGuardian = useSelector(isParentGuardianSel);
  const childUserId = useSelector(childUserIdSel);
  const isCouncilOrgActive = useSelector(isCouncilOrgActiveSel);
  const isKey3 = useSelector(isKey3Sel);
  const unitInfo = useSelector(unitInfoSel);
  const [subMenuExpanded, setSubMenuExpanded] = useState<string | null>(
    isSettingsRoute || isReportsRoute ? pathname : null,
  );
  const [activeOption, setActiveOption] = useState(pathname);

  const shouldShowCalendar = useSelector((state: RootState) => {
    const permission = permissionsSel(state);
    const isParentGuardian = isParentGuardianSel(state);
    const unitInfo = unitInfoSel(state);
    const [newlySelectedUnit] = state.context.organizationGuid.split('*');

    if (sessionStorage.getItem(`unit_betacalendar_${newlySelectedUnit}`))
      return true;

    if (isParentGuardian || permission.VIEW_CALENDAR_PAGE) {
      return true;
    }

    if (!unitInfo) return false;

    const unitGuid = unitInfo.organizationGuid;
    if (sessionStorage.getItem(`unit_betacalendar_${unitGuid}`)) return true;

    return true;
  });
  const showCustomReportsSubmenu = featureFlags.getFlag(
    'SBL_3440_CUSTOM_REPORTS',
  );

  useEffect(() => {
    if (shouldShowCalendar && unitInfo?.organizationGuid) {
      sessionStorage.setItem(
        `unit_betacalendar_${unitInfo.organizationGuid}`,
        `${true}`,
      );
    }
  }, [shouldShowCalendar, unitInfo.organizationGuid]);

  useEffect(() => {
    if (isSettingsRoute) {
      setSubMenuExpanded('/settings');
    }
  }, [isSettingsRoute]);

  const hasPerms = useCallback(
    permission => {
      const permArray = Array.isArray(permission) ? permission : [permission];
      return permArray.some(perm => userPermissions[perm]);
    },
    [userPermissions],
  );

  const shouldEnableRoster = () =>
    userPermissions[permissions.VIEW_ROSTER_PAGE] && isSelectedUnitValid;

  const shouldShowRoster = () =>
    userPermissions[permissions.VIEW_ROSTER_PAGE] ||
    userPermissions[permissions.VIEW_COUNCIL_UNITS_PAGE];

  const shouldShowUnitPaymentLogs = () =>
    (featureFlags.getFlag('SHOW_UNIT_PAYMENT_LOGS') ||
      overrideShowUnitPaymentLogs) &&
    (userPermissions[permissions.VIEW_UNIT_PAYMENT_LOGS] || isKey3);

  const shouldEnablePermissionsManager = () =>
    userPermissions[permissions.VIEW_PERMISSIONS_MANAGER] &&
    isSelectedUnitValid;

  const handleMenuClick = useCallback(
    ({
      item,
      key,
    }: {
      item: { props: { selectable: boolean } };
      key: string;
    }) => {
      const { selectable = true } = item.props;
      if (selectable) {
        if (subMenuExpanded && !key.includes(subMenuExpanded)) {
          setSubMenuExpanded(null);
        }

        setActiveOption(key);
      }
    },
    [subMenuExpanded],
  );

  const handleOpenRosterReport = () => {
    window.open(getNewRosterReportUri(unitTypeId), '_blank');
  };

  const handleOpenProgressReport = () => {
    window.open(getNewProgressReportUri(unitTypeId), '_blank');
  };

  // key of title
  const handleSubMenuExpand = ({ key }: { key: string; domEvent: Event }) => {
    if (key === '/settings') {
      dispatch(navigateToSettings());
    }
    if (key === '/reports') {
      dispatch(navigateToReports());
    }
    setSubMenuExpanded(key);
    setActiveOption(key);
  };

  const openSubMenuKeys = useMemo(
    () => (subMenuExpanded && isExpanded ? [subMenuExpanded] : []),
    [subMenuExpanded, isExpanded],
  );
  const showLabels = isMobile || isExpanded;
  const showRoster = shouldShowRoster();
  const youthProfileLink =
    isParentGuardian && childUserId ? `/youthProfile/${childUserId}` : '';

  const reportMenu = showCustomReportsSubmenu ? (
    <SubMenu
      key="/reports"
      className={cn(styles.navSubMenuGen, {
        [styles.navSubMenuGenExpanded]: subMenuExpanded === '/reports',
      })}
      title={
        <NavMenuItemContent
          icon={<MdContentPaste />}
          containerClassNames={cn(styles.navSection)}
          showLabels={showLabels}
          loading={loading}
        >
          <FormattedMessage id="page.reports" />
        </NavMenuItemContent>
      }
      disabled={loading}
      onTitleClick={handleSubMenuExpand}
    >
      <MenuItem
        key="/reports/rosterReport"
        selectable={false}
        onClick={handleOpenRosterReport}
      >
        <FormattedMessage id="page.newRosterReport" />
      </MenuItem>
      <MenuItem
        key="/reports/progressReport"
        selectable={false}
        onClick={handleOpenProgressReport}
      >
        <FormattedMessage id="page.newProgressReport" />
      </MenuItem>
    </SubMenu>
  ) : (
    <NavMenuItem
      key="/reports"
      disabled={loading || !userPermissions[permissions.VIEW_REPORTS_PAGE]}
    >
      <NavLink to="/reports" id="qa_reports">
        <NavMenuItemContent
          icon={<MdContentPaste />}
          showLabels={showLabels}
          loading={loading}
        >
          <FormattedMessage id="page.reports" />
        </NavMenuItemContent>
      </NavLink>
    </NavMenuItem>
  );

  return (
    <Menu
      mode="inline"
      className={cn(styles.menu, {
        [styles.subMenuActive]: subMenuExpanded,
      })}
      defaultSelectedKeys={[pathname]}
      openKeys={openSubMenuKeys}
      selectedKeys={[activeOption]}
      onClick={handleMenuClick}
    >
      {hasPerms(permissions.VIEW_COUNCIL_UNITS_PAGE) && (
        <NavMenuItem
          key="/units"
          inlineCollapsed={!showLabels}
          disabled={loading}
        >
          <NavLink to="/units">
            <NavMenuItemContent
              icon={<MdPlace />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.units" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {!!SHOW_DASHBOARD && (
        <NavMenuItem key="/" inlineCollapsed={!showLabels} disabled={loading}>
          <NavLink to="/">
            <NavMenuItemContent
              icon={<MdDashboard />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.dashboard" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {!!showRoster && (
        <NavMenuItem key="/roster" disabled={loading || !shouldEnableRoster()}>
          <NavLink to="/roster" id="qa_roster">
            <NavMenuItemContent
              icon={<MdFormatListBulleted />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.roster" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {!!shouldShowCalendar && hasPerms(permissions.VIEW_CALENDAR_PAGE) && (
        <NavMenuItem
          key="/calendar"
          disabled={
            loading ||
            isInactiveUnit ||
            !userPermissions[permissions.VIEW_CALENDAR_PAGE] ||
            isCouncilOrgActive
          }
        >
          <NavLink to="/calendar">
            <NavMenuItemContent
              icon={<MdToday />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.calendar" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {!!(SHOW_MESSAGING && hasPerms(permissions.VIEW_MESSAGING)) && (
        <NavMenuItem key="/messaging" disabled={loading}>
          <NavLink to="/messaging" id="qa_messaging">
            <NavMenuItemContent
              icon={<MdEmail />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.messaging" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {!!SHOW_MB_COUNSELORS &&
        hasPerms(permissions.VIEW_MERIT_BADGE_COUNSELORS_PAGE) && (
          <NavMenuItem key="/meritBadgeCounselors">
            <NavLink to="/meritBadgeCounselors" id="qa_meritBadgeCounselors">
              <NavMenuItemContent
                icon={<MdHowToReg />}
                showLabels={showLabels}
                loading={loading}
              >
                <FormattedMessage id="core.Nav.meritBadgeCounselors" />
              </NavMenuItemContent>
            </NavLink>
          </NavMenuItem>
        )}
      {!!(
        SHOW_COUNSELED_YOUTH && hasPerms(permissions.VIEW_COUNSELED_YOUTH)
      ) && (
        <NavMenuItem key="/counseledYouth">
          <NavLink to="/counseledYouth" id="qa_counseledYouth">
            <NavMenuItemContent
              icon={<MdSupervisorAccount />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.counseledYouth" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {!!isParentGuardian && (
        <NavMenuItem
          key={youthProfileLink}
          disabled={loading || !userPermissions[permissions.VIEW_PROFILE_PAGE]}
        >
          <NavLink to={youthProfileLink} id="qa_youth_profile">
            <NavMenuItemContent
              icon={<MdFace />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.youthProfile" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {shouldShowUnitPaymentLogs() && (
        <NavMenuItem disabled={loading} key="/unitPaymentLogs">
          <NavLink id="unit_payment_logs" to="/unitPaymentLogs">
            <NavMenuItemContent
              icon={<MdAttachMoney />}
              loading={loading}
              showLabels={showLabels}
            >
              <FormattedMessage id="core.Nav.unitPaymentLogs" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      <NavMenuItem
        key="/profile"
        disabled={loading || !userPermissions[permissions.VIEW_PROFILE_PAGE]}
      >
        <NavLink to="/profile" id="qa_profile">
          <NavMenuItemContent
            icon={<MdFace />}
            showLabels={showLabels}
            loading={loading}
          >
            <FormattedMessage id="page.profile" />
          </NavMenuItemContent>
        </NavLink>
      </NavMenuItem>
      {hasPerms([
        permissions.VIEW_ROSTER_PAGE,
        permissions.VIEW_REPORTS_PAGE,
      ]) && reportMenu}
      {!!(showRoster && SHOW_ACTIVITIES_MENU) && (
        <NavMenuItem
          key="/activities"
          disabled={loading || isInactiveUnit || !shouldEnableRoster()}
        >
          <NavLink to="/activities" id="qa_activities">
            <NavMenuItemContent
              icon={<ActivityIcon />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.activities" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {hasPerms(permissions.SHOW_FORUM_LINK) && (
        <ForumMenuItem showLabels={showLabels} loading={loading} />
      )}
      {hasPerms(permissions.SHOW_SCOUTBOOK_LINK) && (
        <ScoutbookLegacyMenuItem showLabels={showLabels} loading={loading} />
      )}
      {!!(
        hasPerms(permissions.EDIT_PERMISSIONS_MANAGER) &&
        SHOW_PERMISSION_MANAGER
      ) && (
        <NavMenuItem
          key="/permissionsManager"
          disabled={loading || !shouldEnablePermissionsManager()}
        >
          <NavLink to="/permissionsManager" id="qa_permissionsManager">
            <NavMenuItemContent
              icon={<MdPeopleOutline />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.permissionsManager" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
      {!!(
        hasPerms(permissions.SHOW_RECHARTER_LINK) &&
        isRecharterPeriod(unitInfo, RECHARTER_DATE_RESTRICTION) &&
        isKey3
      ) && (
        <NavMenuItem
          key="startRecharter"
          id="qa_startRecharter"
          inlineCollapsed={!showLabels}
          disabled={loading}
        >
          <NavLink to="/recharter">
            <NavMenuItemContent
              icon={<MdInfoOutline />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="page.startRecharter" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}

      {isMobile ? (
        <SubMenu
          key="/settings"
          className={cn(
            styles.navOption,
            styles.navSection,
            styles.navSubMenu,
            styles.settingsMenu,
            {
              [styles.alwaysActive]: isSettingsRoute,
              Joyride__SettingsMenu: isMobile,
            },
          )}
          title={
            <NavMenuItemContent
              icon={<MdSettings />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="core.Nav.settingsMenu" />
            </NavMenuItemContent>
          }
          disabled={loading}
          onTitleClick={handleSubMenuExpand}
        >
          <SettingsOptions isMobile={true} onClick={handleMenuClick} />
        </SubMenu>
      ) : (
        <NavMenuItem
          key="/settings"
          disabled={loading}
          className={cn(styles.settings)}
        >
          <NavLink to="/settings" id="qa_settings">
            <NavMenuItemContent
              icon={<MdSettings />}
              showLabels={showLabels}
              loading={loading}
            >
              <FormattedMessage id="core.Header.Settings.settings" />
            </NavMenuItemContent>
          </NavLink>
        </NavMenuItem>
      )}
    </Menu>
  );
};

export default Nav;
